import React, { memo, useState } from "react";

import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Grid,
  Card,
  makeStyles,
  IconButton
} from "@material-ui/core";

import CircleNumber from "../common/CircleNumber";
import Typography from "@material-ui/core/Typography";
import { remCalc, downloadStops } from "../../utils/helpers";

import { useSelector } from "react-redux";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import OptimizedTooltip from "../common/OptimizedTooltip";

const useStyles = makeStyles(theme => ({
  gridItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    "& .download-stops": {
      position: "absolute",
      top: "16%",
      background: "#F7F7F7",
      display: "none"
    },
    "&:hover .download-stops": {
      display: "flex"
    }
  },
  accordionSummary: {
    padding: "0.125rem 1rem",
    "& > div": {
      justifyContent: "space-between",
      alignItems: "center"
    }
  },
  accordionDetails: {
    padding: 0
  },
  progressPercent: {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: "#7D7E82",
    lineHeight: "0.9375rem",
    fontFamily: "Roboto"
  },
  progressDiff: {
    fontSize: "0.8125rem",
    fontFamily: "Roboto",
    color: "#202124",
    fontWeight: 500,
    margin: `0 ${theme.spacing(2)}`
  },
  subtitle: {
    fontWeight: 500,
    fontSize: remCalc(14),
    marginTop: theme.spacing(1)
  },
  circleNumber: {
    fontWeight: 600,
    fontSize: "1rem",
    lineHeight: "1.875rem"
  },
  progressDivider: {
    height: "auto",
    marginLeft: "0.75rem"
  },
  stopStatsCard: {
    position: "absolute",
    top: "12px",
    left: "474px",
    minWidth: "324px"
  },
  popper: {
    paddingTop: "45px"
  }
}));

const DownloadStops = ({ stops, toolTipMessage, stopsType }) => {
  const classes = useStyles();
  const handleStopsDownload = () => downloadStops(stops, stopsType);

  return (
    <Box className="download-stops">
      <OptimizedTooltip
        classes={{ popper: classes.popper }}
        title={toolTipMessage}
      >
        <IconButton onClick={handleStopsDownload}>
          <SaveAltIcon />
        </IconButton>
      </OptimizedTooltip>
    </Box>
  );
};

function StopStats() {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const solution = useSelector(state => state.project.solution);

  const stops = solution?.routes.flatMap(route => route.stops);

  let finishedStops = [];
  let failedStops = [];
  let remainingStops = [];
  stops.forEach(stop => {
    const { type, unassigned, status } = stop;
    const isRouteStop =
      !["start", "break", "end"].includes(type) && !unassigned;
    if (isRouteStop) {
      switch (status) {
        case "finished":
          finishedStops.push(stop);
          break;
        case "skipped":
          failedStops.push(stop);
          break;
        default:
          remainingStops.push(stop);
          break;
      }
    }
  });

  const finishedStopsCount = finishedStops.length;
  const failedStopsCount = failedStops.length;
  const remainingStopsCount = remainingStops.length;
  const allStopsCount =
    finishedStops.length + failedStops.length + remainingStops.length;
  const successAndFailedStopsCount = failedStopsCount + finishedStopsCount;

  const progressPercent = Math.round(
    (successAndFailedStopsCount / allStopsCount) * 100
  );

  return (
    allStopsCount > 0 && (
      <Card className={classes.stopStatsCard}>
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(expanded => !expanded)}
          TransitionProps={{ unmountOnExit: true }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <>
              <Box>
                <Typography
                  component="span"
                  className={classes.progressPercent}
                >{`Progress (${progressPercent}%)`}</Typography>
              </Box>
              <Box display="flex">
                <Typography
                  component="p"
                  className={classes.progressCount}
                >{`${successAndFailedStopsCount}/${allStopsCount}`}</Typography>
                <Divider
                  className={classes.progressDivider}
                  orientation="vertical"
                />
              </Box>
            </>
          </AccordionSummary>
          <Divider />
          <AccordionDetails className={classes.accordionDetails}>
            <Box width="100%" pt={2} pb={2} px={3}>
              <Box component={Grid} container spacing={2}>
                {/* Success */}
                <Grid item xs={4} className={classes.gridItem}>
                  <CircleNumber
                    text={finishedStopsCount}
                    size={56}
                    color="#11B163"
                    className={classes.circleNumber}
                  />
                  {finishedStopsCount > 0 && (
                    <DownloadStops
                      stops={finishedStops}
                      toolTipMessage="Download success stops"
                      stopsType="success"
                    />
                  )}
                  <Typography className={classes.subtitle}>Success</Typography>
                </Grid>

                {/* Failed */}
                <Grid item xs={4} className={classes.gridItem}>
                  <CircleNumber
                    text={failedStopsCount}
                    size={56}
                    color="#F50057"
                    className={classes.circleNumber}
                  />
                  {failedStopsCount > 0 && (
                    <DownloadStops
                      stops={failedStops}
                      toolTipMessage="Download failed stops"
                      stopsType="failed"
                    />
                  )}
                  <Typography className={classes.subtitle}>Failed</Typography>
                </Grid>

                {/* Remaining */}
                <Grid item xs={4} className={classes.gridItem}>
                  <CircleNumber
                    text={remainingStopsCount}
                    size={56}
                    className={classes.circleNumber}
                  />
                  {remainingStopsCount > 0 && (
                    <DownloadStops
                      stops={remainingStops}
                      toolTipMessage="Download remaining stops"
                      stopsType="remaining"
                    />
                  )}
                  <Typography className={classes.subtitle}>
                    Remaining
                  </Typography>
                </Grid>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Card>
    )
  );
}

export default memo(StopStats);